/* ============================================================================
   VILLAINS TCG — Base Web Component (cst-base-element.js)
   Lightweight base class every cst-* custom element extends. Progressive
   enhancement: markup renders server-side (Liquid); this only adds behavior.
   Self-contained (no importmap); reads shared helpers from window.CST.
   Ref: MASTER-IMPLEMENTATION-BLUEPRINT.md §13 · Phase 1 §6 (web components)
   ========================================================================== */

export class CstElement extends HTMLElement {
  constructor() {
    super();
    /** Disposers collected via this.listen(); auto-cleaned on disconnect. */
    this._disposers = [];
  }

  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;
    // Defer to next frame so inner markup is parsed before mount().
    requestAnimationFrame(() => {
      if (this.isConnected) this.mount();
    });
  }

  disconnectedCallback() {
    this._disposers.forEach((off) => {
      try { off(); } catch (_) { /* noop */ }
    });
    this._disposers = [];
    this._mounted = false;
    this.unmount();
  }

  /** Override: set up behavior here. */
  mount() {}
  /** Override: tear down here (listeners added via listen() auto-clean). */
  unmount() {}

  /** Add a managed event listener (auto-removed on disconnect). */
  listen(target, type, handler, options) {
    const off = (t, ty, h, o) => {
      t.addEventListener(ty, h, o);
      return () => t.removeEventListener(ty, h, o);
    };
    const dispose = off(target, type, handler, options);
    this._disposers.push(dispose);
    return dispose;
  }

  /** First matching descendant. */
  ref(selector) { return this.querySelector(selector); }
  /** All matching descendants. */
  refs(selector) { return Array.from(this.querySelectorAll(selector)); }

  /** Respect reduced-motion in JS (not just CSS). */
  get reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Dispatch a namespaced custom event. */
  emit(name, detail) {
    return this.dispatchEvent(
      new CustomEvent(name, { detail, bubbles: true, composed: true, cancelable: true })
    );
  }
}

/** Safe register — no-op if the tag is already defined. */
export function defineElement(tagName, elementClass) {
  if (!customElements.get(tagName)) customElements.define(tagName, elementClass);
  return elementClass;
}

/* Share on the CST namespace for components that don't import via ESM. */
window.CST = Object.assign(window.CST || {}, { Element: CstElement, defineElement });

export default CstElement;
